import { type Component, type JSX, batch } from "solid-js"
import { createStore, produce, reconcile } from "solid-js/store"
import type { Agent, AgentType, Analysis, ClockAnnotation, ScenarioMeta, SpeedControl, ViewportPin } from "../../types"
import { AGENT_EMOJIS } from "../../types"
import { scheduleSave as _scheduleSave, loadProject, saveProject } from "../persistence"
import { ScenarioContext } from "./context"
import type { ScenarioActions, ScenarioState } from "./types"



interface ScenarioProviderProps {
    children: JSX.Element
}

// Global state reference for non-component access (e.g., animation.ts)
// This allows utility files to read scenario data without using hooks
let globalScenarioState: ScenarioState | null = null

/**
 * Get the global scenario state for use in non-component files
 * @returns Current scenario state or throws if not initialized
 */
export function getScenarioState(): ScenarioState {
    if (!globalScenarioState) {
        throw new Error("Scenario store not initialized")
    }
    return globalScenarioState
}

/**
 * ScenarioProvider - Provides scenario store to component tree
 *
 * Uses SolidJS createStore + produce for immutable updates (immer-style)
 */
export const ScenarioProvider: Component<ScenarioProviderProps> = (props) => {
    // Create unified store with initial values from old stores
    const [state, setState] = createStore<ScenarioState>({
        meta: {
            version: 1,
            name: "",
            startTime: { hour: 23, minute: 6, second: 30 },
            crimeTime: { hour: 23, minute: 21, second: 30 },
            soundEffects: true,
        },
        agents: [],
        viewportPins: [],
        clockAnnotations: [],
        speedControls: [],
        analysis: { scenarioName: "", ingress: "", sections: [] },
        autoPan: true,
        loadCount: 0,
    })

    // Set global reference for non-component access
    globalScenarioState = state

    // Actions using produce() for immutable updates
    const actions: ScenarioActions = {
        // Meta actions
        updateMeta: (updates: Partial<ScenarioMeta>) => {
            setState("meta", updates)
        },

        // Agent actions
        addAgent: (type: AgentType) => {
            const agent: Agent = {
                id: `agent-${Date.now()}`,
                type,
                name: `${type.charAt(0).toUpperCase() + type.slice(1)} ${state.agents.length + 1}`,
                label: `${type.substring(0, 3).toUpperCase()}`,
                track: "",
                testimony: "",
                speed: 1.4,
                segments: [],
                color: "#3b82f6",
                emoji: AGENT_EMOJIS[type],
                sources: [],
            }
            setState(
                "agents",
                produce((a) => a.push(agent))
            )
        },

        updateAgent: (id: string, updates: Partial<Omit<Agent, "id">>) => {
            setState("agents", (a) => a.id === id, updates)
        },

        removeAgent: (id: string) => {
            setState("agents", (agents) => agents.filter((a) => a.id !== id))
        },

        duplicateAgent: (id: string) => {
            const agent = state.agents.find((a) => a.id === id)
            if (!agent) return

            const duplicate: Agent = {
                ...agent,
                id: `agent-${Date.now()}`,
                name: `${agent.name} (kopia)`,
            }
            setState(
                "agents",
                produce((a) => a.push(duplicate))
            )
        },

        addSegment: (agentId: string) => {
            setState(
                "agents",
                (a) => a.id === agentId,
                produce((agent) => {
                    const segs = agent.segments
                    if (segs.length === 0) {
                        // First segment starts at scenario start time (will be filled in by scenario loader)
                        agent.segments.push({
                            timeMode: "start",
                            startTime: { hour: 23, minute: 0, second: 0 },
                            endTime: { hour: 23, minute: 6, second: 0 },
                            waypoints: [],
                        })
                        return
                    }

                    // Helper to convert SimTime to seconds
                    const simTimeToSeconds = (t: any) => t.hour * 3600 + t.minute * 60 + t.second
                    const secondsToSimTime = (s: number) => ({
                        hour: Math.floor(s / 3600) % 24,
                        minute: Math.floor((s % 3600) / 60),
                        second: s % 60,
                    })

                    const lastSeg = segs[segs.length - 1]
                    const lastWp = lastSeg.waypoints[lastSeg.waypoints.length - 1]

                    // New segment starts after the last segment ends
                    const lastEndSeconds = simTimeToSeconds(lastSeg.endTime)
                    const newStartSeconds = lastEndSeconds + 60 // 1 minute after previous
                    const newEndSeconds = newStartSeconds + 360 // 6 minutes duration

                    agent.segments.push({
                        timeMode: "start",
                        startTime: secondsToSimTime(newStartSeconds),
                        endTime: secondsToSimTime(newEndSeconds),
                        waypoints: lastWp
                            ? [{ position: [...lastWp.position] as [number, number], stopDuration: 0 }]
                            : [],
                    })
                })
            )
        },

        removeSegment: (agentId: string, segIdx: number) => {
            setState(
                "agents",
                (a) => a.id === agentId,
                "segments",
                (segs) => segs.filter((_, i) => i !== segIdx)
            )
        },

        setSegmentStartTime: (agentId: string, segIdx: number, time: any) => {
            setState("agents", (a) => a.id === agentId, "segments", segIdx, "startTime", time)
        },

        setSegmentEndTime: (agentId: string, segIdx: number, time: any) => {
            setState("agents", (a) => a.id === agentId, "segments", segIdx, "endTime", time)
        },

        setSegmentTimeMode: (agentId: string, segIdx: number, mode: "start" | "end") => {
            setState("agents", (a) => a.id === agentId, "segments", segIdx, "timeMode", mode)
        },

        addSource: (agentId: string) => {
            setState(
                "agents",
                (a) => a.id === agentId,
                produce((agent) => {
                    if (!agent.sources) agent.sources = []
                    agent.sources.push({
                        id: `source-${Date.now()}`,
                        name: "",
                        url: "",
                    })
                })
            )
        },

        removeSource: (agentId: string, sourceId: string) => {
            setState(
                "agents",
                (a) => a.id === agentId,
                "sources",
                (sources) => (sources || []).filter((s) => s.id !== sourceId)
            )
        },

        updateSource: (agentId: string, sourceId: string, updates: any) => {
            setState(
                "agents",
                (a) => a.id === agentId,
                "sources",
                (s) => s.id === sourceId,
                updates
            )
        },

        // Waypoint actions
        addWaypoint: (agentId: string, position: [number, number], segmentIdx?: number) => {
            setState(
                "agents",
                (a) => a.id === agentId,
                produce((agent) => {
                    const segment =
                        segmentIdx !== undefined
                            ? agent.segments[segmentIdx]
                            : agent.segments[agent.segments.length - 1]
                    if (segment) {
                        segment.waypoints.push({
                            position,
                            stopDuration: 0,
                        })
                    }
                })
            )
        },

        updateWaypoint: (agentId: string, segIdx: number, wpIdx: number, updates: any) => {
            setState("agents", (a) => a.id === agentId, "segments", segIdx, "waypoints", wpIdx, updates)
        },

        removeWaypoint: (agentId: string, segIdx: number, wpIdx: number) => {
            setState(
                "agents",
                (a) => a.id === agentId,
                "segments",
                segIdx,
                "waypoints",
                (waypoints: any[]) => waypoints.filter((_, i) => i !== wpIdx)
            )
        },

        updateWaypointPosition: (agentId: string, segIdx: number, wpIdx: number, position: [number, number]) => {
            setState("agents", (a) => a.id === agentId, "segments", segIdx, "waypoints", wpIdx, "position", position)
        },

        setWaypointSpeed: (agentId: string, segIdx: number, wpIdx: number, speed: number | undefined) => {
            setState("agents", (a) => a.id === agentId, "segments", segIdx, "waypoints", wpIdx, "speed", speed)
        },

        setWaypointStopDuration: (agentId: string, segIdx: number, wpIdx: number, duration: number) => {
            setState(
                "agents",
                (a) => a.id === agentId,
                "segments",
                segIdx,
                "waypoints",
                wpIdx,
                "stopDuration",
                Math.max(0, duration)
            )
        },

        // ViewportPin actions
        addViewportPin: (isFirstPin = false) => {
            const newPin: ViewportPin = {
                id: isFirstPin ? "viewport-first" : `viewport-${Date.now()}`,
                time: isFirstPin ? "default-start" : { hour: 23, minute: 0, second: 0 },
                position: [0, 0],
                zoom: 18,
            }
            setState(
                "viewportPins",
                produce((p) => p.push(newPin))
            )
        },

        updateViewportPin: (id: string, updates: Partial<Omit<ViewportPin, "id">>) => {
            setState("viewportPins", (p) => p.id === id, updates)
        },

        removeViewportPin: (id: string) => {
            // Prevent deletion of default start pin
            const pin = state.viewportPins.find((p) => p.id === id)
            if (pin?.time === "default-start") return

            setState("viewportPins", (pins) => pins.filter((p) => p.id !== id))

            // Ensure at least one pin remains
            if (state.viewportPins.length === 0) {
                actions.addViewportPin(true)
            }
        },

        setAutoPan: (enabled: boolean) => {
            setState("autoPan", enabled)
        },

        // ClockAnnotation actions
        addClockAnnotation: () => {
            const annotation: ClockAnnotation = {
                id: `annotation-${Date.now()}`,
                time: { hour: 23, minute: 0, second: 0 },
                text: "",
            }
            setState(
                "clockAnnotations",
                produce((a) => a.unshift(annotation))
            )
        },

        updateClockAnnotation: (id: string, updates: Partial<Omit<ClockAnnotation, "id">>) => {
            setState("clockAnnotations", (a) => a.id === id, updates)
        },

        removeClockAnnotation: (id: string) => {
            setState("clockAnnotations", (annotations) => annotations.filter((a) => a.id !== id))
        },

        // SpeedControl actions
        addSpeedControl: () => {
            const newControl: SpeedControl = {
                id: `speed-${Date.now()}`,
                time: { hour: 23, minute: 0, second: 0 },
                speedMultiplier: 1,
            }
            setState(
                "speedControls",
                produce((s) => s.push(newControl))
            )
        },

        updateSpeedControl: (id: string, updates: Partial<Omit<SpeedControl, "id">>) => {
            setState("speedControls", (s) => s.id === id, updates)
        },

        removeSpeedControl: (id: string) => {
            setState("speedControls", (controls) => controls.filter((s) => s.id !== id))
        },

        // Analysis actions
        updateAnalysis: (updates: Partial<Analysis>) => {
            setState("analysis", updates)
        },

        setAnalysisScenarioName: (name: string) => {
            setState("analysis", "scenarioName", name)
        },

        setAnalysisIngress: (ingress: string) => {
            setState("analysis", "ingress", ingress)
        },

        addAnalysisSection: () => {
            const newSection = {
                id: `analysis-${Date.now()}`,
                time: { hour: 23, minute: 0, second: 0 },
                order: state.analysis.sections.length,
                headline: "",
                content: "",
            }
            setState(
                "analysis",
                "sections",
                produce((s) => s.push(newSection))
            )
        },

        updateAnalysisSection: (id: string, updates: any) => {
            const index = state.analysis.sections.findIndex((s) => s.id === id)
            if (index !== -1) {
                setState("analysis", "sections", index, updates)
            }
        },

        removeAnalysisSection: (id: string) => {
            setState("analysis", "sections", (prev) => prev.filter((s) => s.id !== id))
            // Reorder remaining sections
            setState("analysis", "sections", (prev) => prev.map((s, idx) => ({ ...s, order: idx })))
        },

        moveAnalysisSection: (id: string, direction: "up" | "down") => {
            const sections = state.analysis.sections
            const index = sections.findIndex((s) => s.id === id)
            if (index === -1) return

            const newIndex = direction === "up" ? index - 1 : index + 1
            if (newIndex < 0 || newIndex >= sections.length) return

            setState("analysis", "sections", (prev) => {
                const newSections = [...prev]
                const [moved] = newSections.splice(index, 1)
                newSections.splice(newIndex, 0, moved)
                return newSections.map((s, idx) => ({ ...s, order: idx }))
            })
        },

        // Batch loaders
        loadScenario: (data: Partial<ScenarioState>) => {
            batch(() => {
                if (data.meta) setState("meta", data.meta)
                if (data.agents) setState("agents", data.agents)
                if (data.viewportPins) setState("viewportPins", data.viewportPins)
                if (data.clockAnnotations) setState("clockAnnotations", data.clockAnnotations)
                if (data.speedControls) setState("speedControls", data.speedControls)
                if (data.analysis) setState("analysis", reconcile(data.analysis))
                if (data.autoPan !== undefined) setState("autoPan", data.autoPan)
                setState("loadCount", (c) => c + 1)
            })
        },

        resetScenario: () => {
            setState({
                meta: {
                    version: 1,
                    name: "",
                    startTime: { hour: 23, minute: 6, second: 30 },
                    crimeTime: { hour: 23, minute: 21, second: 30 },
                    soundEffects: true,
                },
                agents: [],
                viewportPins: [],
                clockAnnotations: [],
                speedControls: [],
                analysis: {
                    scenarioName: "",
                    ingress: "",
                    sections: [],
                },
                autoPan: true,
            })
        },

        // Persistence actions
        saveToLocalStorage: () => {
            saveProject(state)
        },

        loadFromLocalStorage: () => {
            const project = loadProject()
            if (project) {
                actions.loadScenario({
                    meta: project.meta,
                    agents: project.agents,
                    viewportPins: project.viewportPins,
                    clockAnnotations: project.clockAnnotations,
                    speedControls: project.speedControls,
                    analysis: project.analysis,
                    autoPan: project.autoPan,
                })

                // Ensure analysis scenarioName is synced with meta.name
                actions.setAnalysisScenarioName(project.meta.name)

                return true
            }
            return false
        },

        scheduleSave: () => {
            _scheduleSave(() => state)
        },
    }

    const store: [ScenarioState, ScenarioActions] = [state, actions]

    return <ScenarioContext.Provider value={store}>{props.children}</ScenarioContext.Provider>
}
